import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Trophy, Activity, ArrowUp, ArrowDown, Target } from "lucide-react";
import { useAuth } from "../App";
import LivePredictionPanel from "./LivePredictions/LivePredictionPanel";
import TeamIcon from "@/components/TeamIcon";
import GlassCard from "./MatchList/components/GlassCard";
import { colors } from "./MatchList/constants";
import { BACKEND_URL } from "@/lib/api";
import { LIVE_POLL_INTERVAL_MS } from "@/constants";

const ON_TRACK_THRESHOLD = 20;

const formatOvers = (v) => {
  if (v == null) return "–";
  const n = Number(v);
  if (Number.isNaN(n)) return "–";
  return n.toFixed(1);
};

const LiveMatch = ({ user }) => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userPrediction, setUserPrediction] = useState(null);

  useEffect(() => {
    fetchMatch();
    checkUserPrediction();
    const interval = setInterval(fetchMatch, LIVE_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [matchId]);

  const fetchMatch = async () => {
    try {
      const fetchFn = auth?.authFetch || fetch;
      const response = await fetchFn(`${BACKEND_URL}/api/matches/${matchId}`);
      const data = await response.json();
      setMatch(data);
    } finally {
      setLoading(false);
    }
  };

  const checkUserPrediction = async () => {
    try {
      const userId = user?.user_id || user?.id;
      const fetchFn = auth?.authFetch || fetch;
      const res = await fetchFn(`${BACKEND_URL}/api/predictions/${matchId}`);
      if (!res.ok) return;
      const data = await res.json();
      const p = (data.predictions || []).find((x) => x.user_id === userId);
      setUserPrediction(p || null);
    } catch (_) {
      /* non-critical */
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: colors.bg }}
      >
        <div className="w-8 h-8 border-2 border-[#01ff9a] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!match) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-5"
        style={{ background: colors.bg }}
      >
        <GlassCard padding="p-6" className="text-center max-w-sm">
          <p className="text-white/60">Match not found</p>
          <button
            onClick={() => navigate("/")}
            className="mt-4 text-sm font-semibold"
            style={{ color: colors.accent }}
          >
            Back to matches →
          </button>
        </GlassCard>
      </div>
    );
  }

  const isLive = match.status === "live";
  const isCompleted = match.status === "completed";
  const totalRuns = match.total_runs ?? 0;
  const predicted = userPrediction?.predicted_runs;
  const diff = predicted != null ? predicted - totalRuns : null;

  let trackStatus = null;
  if (predicted != null && (isLive || isCompleted)) {
    if (Math.abs(diff) <= ON_TRACK_THRESHOLD) {
      trackStatus = { label: "On Track", color: colors.onTrack, Icon: Target };
    } else if (diff > 0) {
      trackStatus = { label: "Pace Behind", color: colors.undershoot, Icon: ArrowDown };
    } else {
      trackStatus = { label: "Overshooting", color: colors.overshoot, Icon: ArrowUp };
    }
  }

  const progressPct = predicted != null
    ? Math.min((totalRuns / predicted) * 100, 120)
    : 0;

  // Infer batting side: 2nd innings → team B is batting; otherwise 1st innings → team A.
  const secondInningsStarted =
    (match.team_b_score != null && match.team_b_score >= 0 && (match.team_b_overs ?? 0) > 0) ||
    (match.team_a_overs ?? 0) >= 20;
  const teamABatting = isLive && !secondInningsStarted;
  const teamBBatting = isLive && secondInningsStarted;

  return (
    <div className="min-h-screen" style={{ background: colors.bg }}>
      {/* Sticky Header */}
      <div
        className="sticky top-0 z-40 backdrop-blur-xl"
        style={{
          background: "rgba(15,15,15,0.78)",
          borderBottom: `1px solid ${colors.cardBorder}`,
        }}
      >
        <div className="max-w-md mx-auto px-5 py-3.5 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 -ml-1 px-1.5 py-1.5 rounded-lg hover:bg-white/5 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 text-white/80" />
            <span className="text-sm text-white/80">Matches</span>
          </button>

          <div className="flex items-center gap-1.5 text-[13px]" style={{ color: colors.textMuted }}>
            <span className="font-medium text-white/70">M{match.match_number}</span>
            <span>·</span>
            <span className="truncate max-w-[140px]">{match.venue || "—"}</span>
          </div>

          {isLive ? (
            <div className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ background: "#ef4444", boxShadow: "0 0 8px #ef4444" }}
              />
              <span className="text-[11px] font-semibold tracking-wider text-[#ef4444]">LIVE</span>
            </div>
          ) : isCompleted ? (
            <span className="text-[11px] font-semibold tracking-wider text-white/40">FT</span>
          ) : (
            <span className="text-[11px] font-semibold tracking-wider text-white/40">SOON</span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-md mx-auto px-5 py-5 space-y-4 pb-24">
        {/* Score Hero Card */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <GlassCard padding="p-[22px]">
            {/* Teams row */}
            <div className="flex items-center justify-between gap-3 mb-5">
              {/* Team A */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5">
                  <div className="relative">
                    <TeamIcon team={match.team_a} size="md" />
                    {teamABatting && (
                      <span
                        className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full animate-pulse"
                        style={{ background: colors.accent, boxShadow: `0 0 6px ${colors.accent}` }}
                      />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wider font-medium text-white/40">
                      {teamABatting ? "Batting" : "Team A"}
                    </p>
                    <p className="text-white font-semibold text-sm truncate max-w-[110px]">
                      {match.team_a}
                    </p>
                  </div>
                </div>
                {match.team_a_score != null ? (
                  <div className="mt-2">
                    <p className="text-[28px] font-bold text-white leading-none tabular-nums">
                      {match.team_a_score}
                      <span className="text-base font-normal text-white/40">
                        /{match.team_a_wickets ?? 0}
                      </span>
                    </p>
                    <p className="text-[11px] text-white/40 mt-1">
                      {formatOvers(match.team_a_overs)} ov
                    </p>
                  </div>
                ) : (
                  <p className="text-[11px] text-white/30 mt-2">Yet to bat</p>
                )}
              </div>

              <div className="px-1 flex-shrink-0 pt-6">
                <span className="text-xs font-medium" style={{ color: colors.textMuted }}>
                  VS
                </span>
              </div>

              {/* Team B */}
              <div className="flex-1 min-w-0 text-right">
                <div className="flex items-center gap-2.5 justify-end">
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wider font-medium text-white/40">
                      {teamBBatting ? "Batting" : "Team B"}
                    </p>
                    <p className="text-white font-semibold text-sm truncate max-w-[110px] ml-auto">
                      {match.team_b}
                    </p>
                  </div>
                  <div className="relative">
                    <TeamIcon team={match.team_b} size="md" />
                    {teamBBatting && (
                      <span
                        className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full animate-pulse"
                        style={{ background: colors.accent, boxShadow: `0 0 6px ${colors.accent}` }}
                      />
                    )}
                  </div>
                </div>
                {match.team_b_score != null ? (
                  <div className="mt-2">
                    <p className="text-[28px] font-bold text-white leading-none tabular-nums">
                      {match.team_b_score}
                      <span className="text-base font-normal text-white/40">
                        /{match.team_b_wickets ?? 0}
                      </span>
                    </p>
                    <p className="text-[11px] text-white/40 mt-1">
                      {formatOvers(match.team_b_overs)} ov
                    </p>
                  </div>
                ) : (
                  <p className="text-[11px] text-white/30 mt-2">Yet to bat</p>
                )}
              </div>
            </div>

            {/* Summary row */}
            <div
              className="flex items-center justify-between p-4 rounded-xl"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: `1px solid ${colors.cardBorder}`,
              }}
            >
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wider text-white/40 mb-1">
                  {isLive ? "Total Match Score" : "Total Runs"}
                </p>
                <div className="flex items-center gap-1.5">
                  {isLive && (
                    <span
                      className="w-1.5 h-1.5 rounded-full animate-pulse"
                      style={{ background: colors.accent }}
                    />
                  )}
                  <span
                    className="text-3xl font-bold tabular-nums"
                    style={{
                      background: "linear-gradient(90deg, #01ff9a, #acffe5)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {totalRuns}
                  </span>
                </div>
                {isLive && match.current_over != null && (
                  <p className="text-[11px] text-white/40 mt-0.5">
                    Current over {formatOvers(match.current_over)}
                  </p>
                )}
              </div>
              {predicted != null && (
                <div className="text-right">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-white/40 mb-1">
                    Your Prediction
                  </p>
                  <p className="text-2xl font-bold text-white tabular-nums">{predicted}</p>
                </div>
              )}
            </div>

            {isCompleted && match.result_text && (
              <div
                className="mt-4 p-3 rounded-xl text-center"
                style={{
                  background: "rgba(1,255,154,0.07)",
                  border: "1px solid rgba(1,255,154,0.2)",
                }}
              >
                <Trophy className="w-4 h-4 mx-auto mb-1" style={{ color: colors.accent }} />
                <p className="text-sm font-semibold text-white">{match.result_text}</p>
              </div>
            )}
          </GlassCard>
        </motion.div>

        {/* Prediction Tracker */}
        {predicted != null && (isLive || isCompleted) && trackStatus && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <GlassCard padding="p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="text-[10px] font-medium uppercase tracking-wider text-white/40">
                  How your prediction is doing
                </p>
                <div
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                  style={{
                    background: `${trackStatus.color}14`,
                    border: `1px solid ${trackStatus.color}33`,
                  }}
                >
                  <trackStatus.Icon className="w-3 h-3" style={{ color: trackStatus.color }} />
                  <span
                    className="text-[11px] font-semibold tracking-wide"
                    style={{ color: trackStatus.color }}
                  >
                    {trackStatus.label}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-4">
                <div>
                  <p className="text-[10px] text-white/40 mb-1">Predicted</p>
                  <p className="text-xl font-bold text-white tabular-nums">{predicted}</p>
                </div>
                <div>
                  <p className="text-[10px] text-white/40 mb-1">Current</p>
                  <p className="text-xl font-bold tabular-nums"
                     style={{ color: colors.accent }}>
                    {totalRuns}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-white/40 mb-1">Difference</p>
                  <p
                    className="text-xl font-bold tabular-nums"
                    style={{ color: trackStatus.color }}
                  >
                    {diff > 0 ? "+" : ""}{diff}
                  </p>
                </div>
              </div>

              {/* Progress bar */}
              <div
                className="h-1.5 rounded-full overflow-hidden relative"
                style={{ background: "rgba(255,255,255,0.06)" }}
              >
                <motion.div
                  className="h-full rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(progressPct, 100)}%` }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  style={{
                    background: `linear-gradient(90deg, ${colors.accent}, ${colors.accentSecondary})`,
                  }}
                />
                {/* target marker */}
                <div
                  className="absolute top-0 bottom-0 w-px"
                  style={{ left: "100%", background: "rgba(255,255,255,0.3)" }}
                />
              </div>
              <div className="flex justify-between mt-1.5">
                <span className="text-[10px] text-white/30">0</span>
                <span className="text-[10px] text-white/40">Target {predicted}</span>
              </div>
            </GlassCard>
          </motion.div>
        )}

        {/* Live Predictions Panel */}
        {isLive && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <LivePredictionPanel match={{ ...match, id: matchId }} user={user} />
          </motion.div>
        )}

        {/* Leaderboards */}
        {(isLive || isCompleted) && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="space-y-2.5"
          >
            <button
              onClick={() => navigate(`/live-leaderboard/${matchId}`)}
              className="w-full flex items-center justify-between px-5 py-4 rounded-2xl transition-all hover:scale-[1.01]"
              style={{
                background: "linear-gradient(90deg, #01ff9a, #acffe5)",
                color: "#0f0f0f",
              }}
            >
              <div className="flex items-center gap-2.5">
                <Activity className="w-5 h-5" />
                <div className="text-left">
                  <p className="text-sm font-bold">Live Leaderboard</p>
                  <p className="text-[11px] font-medium opacity-70">Checkpoint points</p>
                </div>
              </div>
              <span className="text-lg font-bold">→</span>
            </button>

            <button
              onClick={() => navigate(`/leaderboard/${matchId}`)}
              className="w-full flex items-center justify-between px-5 py-4 rounded-2xl transition-all hover:scale-[1.01]"
              style={{
                background: colors.cardBg,
                border: `1px solid ${colors.cardBorder}`,
                color: colors.textPrimary,
              }}
            >
              <div className="flex items-center gap-2.5">
                <Trophy className="w-5 h-5 text-white/60" />
                <div className="text-left">
                  <p className="text-sm font-semibold">Pre-match Leaderboard</p>
                  <p className="text-[11px] text-white/40">Total-runs prediction</p>
                </div>
              </div>
              <span className="text-base text-white/40">→</span>
            </button>
          </motion.div>
        )}

        {!isLive && !isCompleted && (
          <GlassCard padding="p-8" className="text-center">
            <p className="text-white/50">Match hasn't started yet.</p>
            <p className="text-xs text-white/30 mt-1">
              Live predictions will open when the match goes live.
            </p>
          </GlassCard>
        )}
      </div>
    </div>
  );
};

export default LiveMatch;
